var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Dropdown, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ACTIVE_CARE_PLAN_DRAFTS from 'apollo/queries/CarePlanDraft/ACTIVE_CARE_PLAN_DRAFTS';
import { useProviderCallStatusWatcher } from 'pages/CarePlanDraft/components/AiCheck/hooks';
import AvailabilityStateIcons from 'pages/CarePlanDraft/components/AiCheck/AvailabilityStateIcons';
import { FACILITIES_PATH, PROVIDERS_PATH } from 'constants/routes';
import { Availability, selectActiveAiCalls, selectDrafts, useCarePlanDraftStore } from 'stores/carePlanDraft';
import { DraftIcon } from 'ui/icons';
import { getNodes } from 'utils/paginationHelper';
import { isNilOrEmpty } from 'utils/helpers';
import useUserStore from 'stores/user';
import { useActiveTicketTypes } from 'components/ActiveTicketTypeSelect/useActiveTicketTypes';
import { stringify } from 'querystringify';
import { transformPreferredDistance } from 'pages/Ticket/CarePlanDetails/helpers';
import { filterEmptyValues } from 'utils/objectHelper';
import { DraftButton, DraftButtonLoader, LabelWrapper } from './styled';
var ActiveCarePlansDropdown = function () {
    var navigate = useNavigate();
    var location = useLocation();
    useProviderCallStatusWatcher({ interval: 20000 });
    var activeAiCalls = useCarePlanDraftStore(selectActiveAiCalls).activeAiCalls;
    var storedDrafts = useCarePlanDraftStore(selectDrafts).drafts;
    var isAiCallInProgress = !isNilOrEmpty(activeAiCalls);
    var isDraftHaveActiveAiCall = function (draftId) {
        return activeAiCalls.some(function (call) { return call.draftId === draftId; });
    };
    var isDraftHaveAiCallResults = function (draftId) {
        var draftByProvider = storedDrafts[draftId];
        if (!draftByProvider)
            return false;
        return Object.values(draftByProvider).some(function (draft) { var _a; return ((_a = draft === null || draft === void 0 ? void 0 : draft.ai_call_result) === null || _a === void 0 ? void 0 : _a.status) !== undefined; });
    };
    var userId = useUserStore(function (s) { return s.userId; });
    var options = useActiveTicketTypes({ disabledOptions: [] }).options;
    var findCareTicketTypeId = useMemo(function () {
        var _a;
        var flattened = options.reduce(function (acc, value) { return acc.concat(value.options); }, []);
        var match = flattened.find(function (option) { return option.label === 'Nav - Find Care'; });
        return (_a = match === null || match === void 0 ? void 0 : match.value) !== null && _a !== void 0 ? _a : null;
    }, [options]);
    var _a = useQuery(ACTIVE_CARE_PLAN_DRAFTS, {
        variables: {
            order: 'reverse_id',
            filters: {
                type_ids: [findCareTicketTypeId],
                assignees: [userId],
                states: ['in_progress_navigator']
            }
        }
    }), loading = _a.loading, data = _a.data;
    var drafts = useMemo(function () {
        var ticketNodes = getNodes(['tickets'])(data);
        if (isNilOrEmpty(ticketNodes))
            return [];
        return ticketNodes.reduce(function (acc, _a) {
            var member = _a.member, resource = _a.resource;
            if (resource.state !== 'in_progress')
                return acc;
            return __spreadArray(__spreadArray([], acc, true), [
                {
                    id: resource.id,
                    performed_by: resource.performed_by,
                    preferred_address: resource.preferred_address,
                    preferred_distance: resource.preferred_distance,
                    title: resource.title,
                    member: member
                }
            ], false);
        }, []);
    }, [data]);
    var _b = useCarePlanDraftStore(function (s) { return ({
        activeDraftId: s.activeDraftId,
        resetActiveDraft: s.resetActiveDraft,
        setActiveDraft: s.setActiveDraft
    }); }), activeDraftId = _b.activeDraftId, resetActiveDraft = _b.resetActiveDraft, setActiveDraft = _b.setActiveDraft;
    var activeDraftType = useMemo(function () { var _a; return (_a = drafts.find(function (d) { return d.id === activeDraftId; })) === null || _a === void 0 ? void 0 : _a.performed_by; }, [drafts, activeDraftId]);
    useEffect(function () {
        if (!isNilOrEmpty(activeDraftType)) {
            var targetPathname = activeDraftType === 'provider' ? PROVIDERS_PATH : FACILITIES_PATH;
            if (location.pathname !== targetPathname)
                resetActiveDraft();
        }
    }, [activeDraftType, location.pathname, resetActiveDraft]);
    var onDraftClick = useCallback(function (draft) {
        var _a;
        setActiveDraft(draft.id);
        var pathname = draft.performed_by === 'provider' ? PROVIDERS_PATH : FACILITIES_PATH;
        var params = filterEmptyValues({
            address: draft === null || draft === void 0 ? void 0 : draft.preferred_address,
            distance: transformPreferredDistance(draft === null || draft === void 0 ? void 0 : draft.preferred_distance),
            member_id: (_a = draft === null || draft === void 0 ? void 0 : draft.member) === null || _a === void 0 ? void 0 : _a.id,
            min_rating: 3,
            is_verified: true
        });
        navigate("".concat(pathname, "?").concat(stringify(params, false)));
    }, [navigate, setActiveDraft]);
    var items = [
        {
            key: '1',
            type: 'group',
            label: 'Active Care Plans',
            children: drafts.map(function (draft) {
                var _a, _b;
                return ({
                    key: draft.id,
                    label: (_jsxs(LabelWrapper, { onClick: function () { return onDraftClick(draft); }, children: [_jsxs("span", { children: [(_a = draft === null || draft === void 0 ? void 0 : draft.member) === null || _a === void 0 ? void 0 : _a.first_name, " ", (_b = draft === null || draft === void 0 ? void 0 : draft.member) === null || _b === void 0 ? void 0 : _b.last_name, " -", ' ', draft === null || draft === void 0 ? void 0 : draft.title] }), _jsx(AvailabilityStateIcons, { loading: isDraftHaveActiveAiCall(draft.id), state: isDraftHaveAiCallResults(draft.id) ? Availability.YES : null })] }))
                });
            })
        }
    ];
    return (_jsx(Dropdown, { menu: { items: items }, trigger: ['click'], disabled: loading, children: _jsx(DraftButton, { "data-test": "activeCarePlanDraftBtn", type: "text", disabled: loading, children: loading ? (_jsx(Spin, { indicator: _jsx(LoadingOutlined, { spin: true }), size: "small" })) : (_jsxs(_Fragment, { children: [_jsx(DraftIcon, {}), isAiCallInProgress && (_jsx(DraftButtonLoader, { children: _jsx(Spin, { indicator: _jsx(LoadingOutlined, { spin: true }), size: "small" }) }))] })) }) }));
};
var FeatureFlagGate = function () {
    var carePlanDraft = useFlags().carePlanDraft;
    if (!carePlanDraft)
        return null;
    return _jsx(ActiveCarePlansDropdown, {});
};
export default React.memo(FeatureFlagGate);
